import { ActivatedRoute, Router } from '@angular/router';
import { AlertController, Platform } from '@ionic/angular/standalone';
import { Component, OnDestroy, signal } from '@angular/core';
import {
  alertCircleOutline,
  chevronBack,
  cogOutline,
  eye,
  helpCircle,
  shareOutline,
  shareSocialOutline,
} from 'ionicons/icons';

import { AppReadyService } from './services/app-ready.service';
import { AuthService } from '@auth0/auth0-angular';
import { LayoutDataService } from './services/layout-data.service';
import { MapDataService } from './services/map-data.service';
import { Network } from '@capacitor/network';
import { Observable } from 'rxjs';
import { PlaceDataService } from './services/place-data.service';
import { RollbarErrorHandler } from './rollbar';
import { SeriesDataService } from './services/series-data.service';
import { SeriesMapDataService } from './services/series-map-data.service';
import { SeriesSubscriptionDataService } from './services/series-subscription-data.service';
import { SplitPaneService } from './services/split-pane.service';
import { SwUpdate } from '@angular/service-worker';
import { SyncStatusStates } from './services/sync-status.service';
import { ToastController } from '@ionic/angular/standalone';
import { TourService } from 'ngx-ui-tour-ionic';
import { User } from './models/user';
import { UserDataService } from './services/user-data.service';
import { addIcons } from 'ionicons';
import { environment } from '../environments/environment';
import { modifiedIsDesktop } from './app.module';

export interface PageInterface {
  title: string;
  url: string;
  icon: string | Array<string>;
  logsOut?: boolean;
  logsIn?: boolean;
  needsAuth?: boolean;
  needsOnline?: boolean;
  count?: number;
  options?;
}

export const RELEASE_NOTES_PAGE: PageInterface = {
  title: 'Release Notes',
  url: '/release-notes',
  icon: ['fad', 'file-alt'],
};
export const SUPPORT_PAGE: PageInterface = {
  title: 'Support',
  url: 'support',
  icon: ['fad', 'question-circle'],
};
export const TUTORIAL_PAGE: PageInterface = {
  title: 'Tutorials',
  url: '/tutorial',
  icon: ['fad', 'chalkboard-teacher'],
};
export const LEGAL_PAGE: PageInterface = {
  title: 'Legal',
  url: '/legal',
  icon: ['fad', 'balance-scale'],
};
export const PLACES_PAGE: PageInterface = {
  title: 'Places',
  url: '/places',
  icon: ['fad', 'border-all'],
  count: 0,
};
export const MAPS_PAGE: PageInterface = {
  title: 'Maps',
  url: '/maps',
  icon: ['fad', 'map'],
  count: 0,
};
export const LAYOUTS_PAGE: PageInterface = {
  title: 'Layouts',
  url: '/layouts',
  icon: null,
  count: 0,
};

export const HOME_PAGE = {
  title: 'Home',
  url: '/',
  icon: ['fad', 'house'],
  count: 0,
  options: { exact: true },
};

/**
 * Application Component
 *
 * Orchestrates offline-first functionality across multiple services.
 *
 * An online connection is required to create the initial user account/profile.
 * After account creation an Auth0 rule creates the Couchbase User account.
 * The user profile is cached on the device in a local api instance.
 *
 * Once the user profile exists locally the user can use the application when offline.
 *
 * When network connectivity is detected:
 * - The Auth0 token is refreshed (possibly requiring an additional login if the access token is long expired).
 * - Users with Masters level accounts have their local api data synchronized (using Auth0 token)
 *   with the remote Couchbase server.
 *   Users w/o Masters level accounts will write a backup to the remote Couchbase server w/o two-way sync.
 *
 * Offline:
 * - Read profile from localStorage to get profile id key used to name local api instance
 *   (this allows multiple users to share the same device).
 *
 * Online:
 *  - Auth0 user sign-up/login
 *  - Store profile to localStorage
 *  - Create/sync local api/remote Couchbase server
 *
 * @export
 * @class AppComponent
 * @implements {OnDestroy}
 */
@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
  standalone: false,
})
export class AppComponent implements OnDestroy {
  public accountPages: PageInterface[] = [
    {
      title: 'Account',
      url: '/account',
      icon: ['fad', 'user-alt'],
      needsAuth: true,
    },
  ];
  public appPages = [HOME_PAGE, LAYOUTS_PAGE, MAPS_PAGE, PLACES_PAGE];
  helpPages: PageInterface[] = [
    TUTORIAL_PAGE,
    SUPPORT_PAGE,
    RELEASE_NOTES_PAGE,
    LEGAL_PAGE,
  ];
  public currentUser: User;
  public showLogin = false;
  public online = signal(true);
  public selectedIndex = 0;
  public seriesPages: PageInterface[] = [
    {
      title: 'All',
      url: '/series',
      icon: ['fad', 'window-restore'],
      needsAuth: true,
    },
    {
      title: 'Latest Maps',
      url: '/series/subscriptions',
      icon: ['fad', 'map'],
      needsAuth: true,
    },
  ];
  public syncStatus: SyncStatusStates = SyncStatusStates.initializing;
  public locallyTriggeredSync = false;
  public displaySynchronized: boolean;

  private _offlineToast: HTMLIonToastElement;

  constructor(
    private alertCtrl: AlertController,
    private layoutData: LayoutDataService,
    private mapData: MapDataService,
    private placeData: PlaceDataService,
    private rollbar: RollbarErrorHandler,
    private route: ActivatedRoute,
    private router: Router,
    private splitPaneService: SplitPaneService,
    private swUpdate: SwUpdate,
    private seriesData: SeriesDataService,
    private seriesSubscriptionData: SeriesSubscriptionDataService,
    private seriesMapData: SeriesMapDataService,
    private toastController: ToastController,
    public tourService: TourService,
    public appReady: AppReadyService,
    public auth: AuthService,
    public platform: Platform,
    public splitPane: SplitPaneService,
    public userData: UserDataService,
  ) {
    this.initializeApp();
    addIcons({
      alertCircleOutline,
      helpCircle,
      eye,
      chevronBack,
      cogOutline,
      shareOutline,
      shareSocialOutline,
    });
  }

  initializeApp(): void {
    // Global iOS keyboard fix - WebKit Bug #254861 workaround
    if (this.platform.is('ios') && 'visualViewport' in window) {
      const webkitWorkaroundHandler = () => {
        window.requestAnimationFrame(() => {
          const viewport = window.visualViewport;
          if (
            viewport &&
            viewport.height ===
              document.documentElement.getBoundingClientRect().height
          ) {
            document.documentElement.scrollTop = 0;
          }
        });
      };
      window.visualViewport?.addEventListener(
        'resize',
        webkitWorkaroundHandler,
      );
    }

    Network.getStatus().then((status) => this.online.set(status.connected));
    Network.addListener('networkStatusChange', async (status) => {
      console.debug('Network status changed', status);
      this.online.set(status.connected);
      if (!this.online() && !this._offlineToast) {
        this._offlineToast = await this.toastController.create({
          message:
            'You appear to be offline. You must be online to add or make changes.',
          color: 'danger',
          buttons: [
            {
              text: 'x',
              role: 'cancel',
            },
          ],
        });
        this._offlineToast.present();
      } else if (this.online() && this._offlineToast) {
        this._offlineToast.dismiss();
        this._offlineToast = null;
      }
      console.debug('online', this.online());
    });
    let everLoggedIn = false;
    this.auth.user$.subscribe(async (user: User) => {
      if (user) {
        if (!this.currentUser || user.id !== this.currentUser.id) {
          console.debug('Auth0 new user:', user);
          this.currentUser = user;
          this.rollbar.addRollbarPerson(user);
          await this.configureAndLoadUser(user);
          everLoggedIn = true;
        }
      } else if (everLoggedIn) {
        console.debug('user logged out');
        // prevent infinite reload loop when Auth0 publishes user === null
        this._logout();
      }
    });
    this.mapData.maps.subscribe((maps) => {
      MAPS_PAGE.count = (maps && maps.length) || 0;
    });
    this.placeData.places.subscribe((places) => {
      PLACES_PAGE.count = (places && places.length) || 0;
    });
    this.layoutData.layouts.subscribe((layouts) => {
      LAYOUTS_PAGE.count = (layouts && layouts.length) || 0;
    });
    this.appReady.syncStatus$.subscribe((status) => {
      // only show toast if we were offline and stay offline until we are synchronized successfully
      this.displaySynchronized =
        this.displaySynchronized ||
        this.locallyTriggeredSync ||
        this.syncStatus === SyncStatusStates.error ||
        this.syncStatus === SyncStatusStates.initializing ||
        !this.online();
      this.syncStatus = status;
      const apiReconnecting =
        status === SyncStatusStates.error && this.online();
      const apiSynchronized =
        status === SyncStatusStates.synchronized && this.displaySynchronized;
      const msg = apiReconnecting
        ? 'Reconnecting to the AC Master database. You can view but not add or update.'
        : apiSynchronized
          ? `AC Master synchronized.`
          : '';
      if (apiSynchronized) {
        this.displaySynchronized = false;
      }
      if (msg) {
        this.toastController
          .create({
            message: msg,
            color: apiReconnecting ? 'danger' : 'light',
            duration: 5000,
          })
          .then((toast) => toast.present());
      }
    });
    if (this.swUpdate.isEnabled) {
      this.swUpdate.versionUpdates.subscribe(async (event) => {
        switch (event.type) {
          case 'VERSION_DETECTED':
            console.debug(`Downloading new app version: ${event.version.hash}`);
            break;
          case 'VERSION_READY':
            console.debug(`Current app version: ${event.currentVersion.hash}`);
            console.debug(
              `New app version ready for use: ${event.latestVersion.hash}`,
            );
            const updateNow = await this.alertCtrl.create({
              header: 'AC Master Update',
              message: 'Load new version now?',
              buttons: [
                {
                  text: 'No',
                  role: 'cancel',
                },
                {
                  text: 'Yes',
                  role: 'yes',
                },
              ],
            });
            await updateNow.present();
            const { role } = await updateNow.onDidDismiss();
            if (role === 'yes') {
              this.swUpdate
                .activateUpdate()
                .then(() => document.location.reload());
            }
            break;
          case 'VERSION_INSTALLATION_FAILED':
            console.error(
              `Failed to install app version '${event.version.hash}': ${event.error}`,
            );
            break;
        }
      });
    }
    // hide login on mobile unless installed as PWA
    this.showLogin = modifiedIsDesktop() || this.platform.is('pwa');

    // watch router query parameters and start tour
    this.route.queryParamMap.subscribe((params) => {
      if (params.has('tour')) {
        this.showTour();
      }
    });
  }

  private async configureAndLoadUser(user: unknown) {
    // set user id and try to load data.
    // if not connected liveSync will ultimately succeed
    await this.userData.loadFromAuthProfile(user);
    await this.sync();
    // If api is down we won't have loaded the user data so don't show tour
    if (
      this.currentUser &&
      this.userData?.user?.email &&
      !this.userData.hasSeenTutorial()
    ) {
      this.showTour();
    }
  }

  async sync(): Promise<void> {
    this.locallyTriggeredSync = true;
    await Promise.allSettled([
      this.userData.liveSync(),
      this.placeData.liveSync(),
      this.mapData.liveSync(),
      this.seriesData.liveSync(),
      this.seriesSubscriptionData.liveSync(),
      this.seriesMapData.liveSync(),
      this.layoutData.liveSync(),
    ]);
    this.locallyTriggeredSync = false;
  }

  showTour(): void {
    this.splitPaneService.show();
    this.tourService.initialize(
      [
        {
          anchorId: 'intro',
          title: 'Welcome!',
          content:
            "Here's a super quick tour that will get you ready to use AC Master.",
          placement: {
            side: 'bottom',
            alignment: 'end',
          },
        },
        {
          anchorId: 'nav1',
          content:
            'These menus let you see and add places, maps, and combine them into layouts.',
          placement: {
            side: 'bottom',
          },
        },
        {
          anchorId: 'nav2',
          content: 'See your info and subscriptions here',
          placement: {
            side: 'bottom',
          },
        },
        {
          anchorId: 'nav3',
          content: "See how-to videos, what's new, or contact me to get help.",
          placement: {
            side: 'top',
          },
        },
        {
          anchorId: 'getStarted',
          content:
            'Next, follow the numbered steps on this home page to get started.',
        },
      ],
      { enableBackdrop: true, popoverClass: 'tour-popover' },
    );
    this.tourService.end$.subscribe(() => {
      this.userData.sawTutorial();
      this.splitPaneService.restore();
    });
    this.tourService.start();
  }

  ngOnDestroy(): void {
    Network.removeAllListeners();
  }

  async loginWithRedirect(): Promise<Observable<void>> {
    // Call this to redirect the user to the login page
    const signedUp = await this.userData.hasSignedUp();
    return this.auth.loginWithRedirect({
      authorizationParams: {
        screen_hint: signedUp ? 'login' : 'signup',
      },
    });
  }

  async toLogin(): Promise<boolean> {
    return this.router.navigateByUrl('/');
  }

  private async _logout() {
    this.userData.logout(); // clear cached User.
    console.error('Logging out', environment.logoutURL);
    this.auth.logout({ logoutParams: { returnTo: environment.logoutURL } }); // redirects to home
  }

  async logout(): Promise<void> {
    this.toastController
      .create({
        message: 'Logged out',
        duration: 3000,
        color: 'light',
      })
      .then((toast) => toast.present());
    await this._logout();
  }
}
